/**
 * Arc sidebar item (AR-578): Mantine NavLink styled after the arcmosaico
 * prototype (components/Layout/Navbar.tsx, translated Mantine 7 -> 6), with
 * prototype active-route treatment, the my-tasks badge (red dot on the icon
 * while collapsed, red badge next to the label while expanded) and SPA
 * navigation for router-owned links so the persistent Arc shell survives.
 */

import { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Box, NavLink, Tooltip } from '@mantine/core'
import { useNavigate } from 'hooks'
import { SidebarMenuIcon } from '../SidebarMenuIcon'
import { IArcSidebarMenuItem, isMenuItemActive } from '../../menus/arcMenus'

/** Cap shown on the count badge to keep it compact. */
const MAX_BADGE_COUNT = 99
/** Size of the red "pending" dot shown on the icon while collapsed. */
const DOT_SIZE = 8
const ITEM_HEIGHT = 44
const ITEM_RADIUS = 8
const ICON_SIZE = 20

interface ArcSidebarMenuItemProps {
  readonly item: IArcSidebarMenuItem
  readonly pathname: string
  readonly collapsed: boolean
  readonly badge?: number
}

export const ArcSidebarMenuItem = ({ item, pathname, collapsed, badge }: ArcSidebarMenuItemProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const label = t(`sidebar_menu.${item.key}`)
  const link = item.link
  const icon = item.icon

  if (link === null || icon === null) return null

  const active = isMenuItemActive(link, pathname)
  const showBadge = Boolean(badge && badge > 0)
  const badgeLabel = badge && badge > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : badge
  const tooltipLabel = showBadge ? `${label} (${badgeLabel})` : label

  const handleClick = item.spaRoute
    ? (event: MouseEvent<HTMLAnchorElement>) => {
      // Keep new-tab/window shortcuts working through the plain anchor
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      event.preventDefault()
      navigate(link)
    }
    : undefined

  const iconNode =
    showBadge && collapsed ? (
      <Box sx={{ position: 'relative', display: 'flex' }}>
        <SidebarMenuIcon icon={icon} size={ICON_SIZE} />
        <Box
          sx={theme => ({
            position: 'absolute',
            top: -2,
            right: -4,
            width: DOT_SIZE,
            height: DOT_SIZE,
            borderRadius: '50%',
            backgroundColor: theme.colors.red[6],
            pointerEvents: 'none',
          })}
        />
      </Box>
    ) : (
      <SidebarMenuIcon icon={icon} size={ICON_SIZE} />
    )

  const rightSection =
    showBadge && !collapsed ? (
      <Badge size='sm' color='red' variant='filled' sx={{ pointerEvents: 'none' }}>
        {badgeLabel}
      </Badge>
    ) : undefined

  return (
    <Tooltip label={tooltipLabel} position='right' withArrow withinPortal disabled={!collapsed}>
      <NavLink
        component='a'
        href={link}
        onClick={handleClick}
        active={active}
        icon={iconNode}
        rightSection={collapsed ? undefined : rightSection}
        label={collapsed ? undefined : label}
        noWrap
        sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
        styles={theme => ({
          // Collapsed: compact 44px rounded square centered in the 60px rail;
          // expanded: full-width 44px pill. Both keep the >= 44px touch target.
          root: collapsed
            ? {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: ITEM_HEIGHT,
              height: ITEM_HEIGHT,
              padding: 0,
              margin: '0 auto',
              borderRadius: ITEM_RADIUS,
              transition: 'background-color 150ms ease',
            }
            : {
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 12,
              paddingRight: 12,
              height: ITEM_HEIGHT,
              borderRadius: ITEM_RADIUS,
              transition: 'background-color 150ms ease',
            },
          icon: collapsed ? { margin: 0, display: 'flex' } : { marginRight: 8, display: 'flex' },
          body: collapsed ? { display: 'none' } : {},
          label: { fontSize: theme.fontSizes.sm, fontWeight: 500 },
        })}
      />
    </Tooltip>
  )
}
